import { useEffect, useRef, type RefObject } from "react";

/**
 * What every dialog in the panel needs, and used to be repeated per dialog in the
 * component: keep focus inside, close with Escape, freeze the page behind it and give the
 * focus back to whatever opened it.
 *
 * Dialogs stack — the confirmation opens over the rule editor — so only the top one
 * answers the keyboard and the page is unfrozen only when the last one closes.
 */

const FOCUSABLE = [
  "button:not([disabled])",
  "a[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

const stack: RefObject<HTMLElement | null>[] = [];

export function useDialog<T extends HTMLElement>(
  onClose: () => void,
  { initialFocus }: { initialFocus?: string } = {},
): RefObject<T | null> {
  const ref = useRef<T>(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    stack.push(ref);
    document.body.style.overflow = "hidden";

    const first = initialFocus ? ref.current?.querySelector<HTMLElement>(initialFocus) : null;
    (first ?? ref.current?.querySelector<HTMLElement>(FOCUSABLE))?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (stack.at(-1) !== ref || !ref.current) return;
      if (event.key === "Escape") {
        event.preventDefault();
        close.current();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = [...ref.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
      const head = focusable[0];
      const tail = focusable.at(-1);
      if (!head || !tail) return;
      if (event.shiftKey && document.activeElement === head) {
        event.preventDefault();
        tail.focus();
      } else if (!event.shiftKey && document.activeElement === tail) {
        event.preventDefault();
        head.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      stack.splice(stack.indexOf(ref), 1);
      if (stack.length === 0) document.body.style.overflow = "";
      previouslyFocused?.focus?.();
    };
    // The dialog's behaviour is set when it opens; `initialFocus` is read once.
  }, []);

  return ref;
}
